import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import invariant from 'invariant';
import isEqual from 'lodash/isEqual';
import { withGoogleMap, GoogleMap, OverlayView, Marker } from 'react-google-maps';
import { OVERLAY_VIEW } from 'react-google-maps/lib/constants';
import MarkerClusterer from 'react-google-maps/lib/components/addons/MarkerClusterer';

import { types as sdkTypes } from '../../util/sdkLoader';
import { parse, stringify } from '../../util/urlHelpers';
import { ensureListing } from '../../util/data';
import { sdkBoundsToFixedCoordinates } from '../../util/maps';
import { groupedByCoordinates, reducedToArray } from './SearchMap.helpers.js';
import {
  SearchMapPriceLabel,
  SearchMapGroupLabel,
} from '../../components';
import pinDktLogo from './map-pin.svg';
import pinExternalLogo from './map-pin-external.svg';

import css from './SearchMap.css';
import mapStyle from './map-style-config';

export const LABEL_HANDLE = 'SearchMapLabel';
export const INFO_CARD_HANDLE = 'SearchMapInfoCard';
const BOUNDS_FIXED_PRECISION = 8;

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

/**
 * Fit part of map (descriped with bounds) to visible map-viewport
 *
 * @param {Object} map - map that needs to be centered with given bounds
 * @param {SDK.LatLngBounds} bounds - the area that needs to be visible when map loads.
 */
export const fitMapToBounds = (map, bounds, options) => {
  const { padding } = options;

  const { ne, sw } = bounds || {};
  // map bounds as string literal for google.maps
  const mapBounds = bounds ? { north: ne.lat, east: ne.lng, south: sw.lat, west: sw.lng } : null;

  // If bounds are given, use it (defaults to center & zoom).
  if (map && mapBounds) {
    if (padding == null) {
      map.fitBounds(mapBounds);
    } else {
      map.fitBounds(mapBounds, padding);
    }
  }
};

/**
 * Convert Google formatted LatLng object to Sharetribe SDK's LatLng coordinate format
 *
 * @param {LatLng} googleLatLng - Google Maps LatLng
 *
 * @return {SDKLatLng} - Converted latLng coordinate
 */
export const googleLatLngToSDKLatLng = googleLatLng => {
  if (!googleLatLng) {
    return null;
  }
  return new SDKLatLng(googleLatLng.lat(), googleLatLng.lng());
};

/**
 * Convert Google formatted bounds object to Sharetribe SDK's bounds format
 *
 * @param {LatLngBounds} googleBounds - Google Maps LatLngBounds
 *
 * @return {SDKLatLngBounds} - Converted bounds
 */
export const googleBoundsToSDKBounds = googleBounds => {
  if (!googleBounds) {
    return null;
  }
  const ne = googleBounds.getNorthEast();
  const sw = googleBounds.getSouthWest();
  return new SDKLatLngBounds(new SDKLatLng(ne.lat(), ne.lng()), new SDKLatLng(sw.lat(), sw.lng()));
};

export const getMapBounds = map => googleBoundsToSDKBounds(map.getBounds());
export const getMapCenter = map => googleLatLngToSDKLatLng(map.getCenter());

/**
 * Check if map library is loaded
 */
export const isMapsLibLoaded = () =>
  typeof window !== 'undefined' && window.google && window.google.maps;

/**
 * FIX "TypeError: Cannot read property 'overlayMouseTarget' of null"
 * Override draw function to catch errors with map panes being undefined to prevent console errors
 * https://github.com/tomchentw/react-google-maps/issues/482
 */
class CustomOverlayView extends OverlayView {
  // onRemove() {
  //   this.containerElement.parentNode.removeChild(this.containerElement);
  //   //Remove `unmountComponentAtNode` for react version 16
  //   //I decided to keep the code here incase React decides not to give out warning when `unmountComponentAtNode` in newer version
  //   if (!React.version.match(/^16/)) {
  //     ReactDOM.unmountComponentAtNode(this.containerElement);
  //   }
  //   this.containerElement = null;
  // }

  onAdd() {
    this.containerElement = document.createElement(`div`);
    this.containerElement.style.position = `absolute`;

    const { mapPaneName } = this.props;
    invariant(
      !!mapPaneName,
      `OverlayView requires either props.mapPaneName or props.defaultMapPaneName but got %s`,
      mapPaneName
    );

    const mapPanes = this.state[OVERLAY_VIEW].getPanes();
    mapPanes[mapPaneName].appendChild(this.containerElement);
    this.onPositionElement();
    this.forceUpdate();
  }

  render() {
    if (React.version.match(/^16/) && this.containerElement) {
      return ReactDOM.createPortal(React.Children.only(this.props.children), this.containerElement);
    }
    return false;
  }

  draw() {
    // https://developers.google.com/maps/documentation/javascript/3.exp/reference#MapPanes
    const mapPanes = this.state[OVERLAY_VIEW].getPanes();
    // Add conditional to ensure panes and container exist before drawing
    if (mapPanes && this.containerElement) {
      this.onPositionElement();
    }
  }
}

/**
 * Center label so that caret is pointing to correct pixel.
 * (vertical positioning: height + arrow)
 */
const getPixelPositionOffset = (width, height) => {
  return { x: -1 * (width / 2), y: -1 * (height + 3) };
};

/**
 * GoogleMaps need to use Google specific OverlayView components and therefore we need to
 * reduce flickering / rerendering of these overlays through 'shouldComponentUpdate'
 */
class SearchMapPriceLabelWithOverlay extends Component {
  shouldComponentUpdate(nextProps) {
    const currentListing = ensureListing(this.props.listing);
    const nextListing = ensureListing(nextProps.listing);
    const isSameListing = currentListing.id.uuid === nextListing.id.uuid;
    const hasSamePrice = currentListing.attributes.price === nextListing.attributes.price;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;

    return !(isSameListing && hasSamePrice && hasSameActiveStatus && hasSameRefreshToken);
  }

  render() {
    const {
      position,
      mapPaneName,
      isActive,
      className,
      listing,
      onListingClicked,
      mapComponentRefreshToken,
    } = this.props;

    return (
      <CustomOverlayView
        position={position}
        mapPaneName={mapPaneName}
        getPixelPositionOffset={getPixelPositionOffset}
      >
        <SearchMapPriceLabel
          isActive={isActive}
          className={className}
          listing={listing}
          onListingClicked={onListingClicked}
          mapComponentRefreshToken={mapComponentRefreshToken}
        />
      </CustomOverlayView>
    );
  }
}

/**
 * GoogleMaps need to use Google specific OverlayView components and therefore we need to
 * reduce flickering / rerendering of these overlays through 'shouldComponentUpdate'
 */
class SearchMapGroupLabelWithOverlay extends Component {
  shouldComponentUpdate(nextProps) {
    const hasSameAmountOfListings = nextProps.listings.length === this.props.listings.length;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;

    return !(hasSameAmountOfListings && hasSameActiveStatus && hasSameRefreshToken);
  }

  render() {
    const {
      position,
      mapPaneName,
      isActive,
      className,
      listings,
      onListingClicked,
      mapComponentRefreshToken,
    } = this.props;
    return (
      <CustomOverlayView
        position={position}
        mapPaneName={mapPaneName}
        getPixelPositionOffset={getPixelPositionOffset}
      >
        <SearchMapGroupLabel
          isActive={isActive}
          className={className}
          listings={listings}
          onListingClicked={onListingClicked}
          mapComponentRefreshToken={mapComponentRefreshToken}
        />
      </CustomOverlayView>
    );
  }
}

const priceLabelsInLocations = (
  listings,
  activeListingId,
  infoCardOpen,
  onListingClicked,
  mapComponentRefreshToken
) => {
  const listingArraysInLocations = reducedToArray(groupedByCoordinates(listings));
  const priceLabels = listingArraysInLocations.reverse().map(listingArr => {
    const isActive = activeListingId
      ? !!listingArr.find(l => activeListingId.uuid === l.id.uuid)
      : false;

    // If location contains only one listing, print price label
    if (listingArr.length === 1) {
      const listing = listingArr[0];
      const infoCardOpenIds = Array.isArray(infoCardOpen) ? infoCardOpen.map(l => l.id.uuid) : [];

      // if the listing is open, don't print price label
      if (infoCardOpen != null && infoCardOpenIds.includes(listing.id.uuid)) {
        return null;
      }

      // Explicit type change to object literal for Google OverlayViews (geolocation is SDK type)
      const { geolocation } = listing.attributes;
      const latLngLiteral = { lat: geolocation.lat, lng: geolocation.lng };

      return (
        <SearchMapPriceLabelWithOverlay
          key={listing.id.uuid}
          position={latLngLiteral}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          isActive={isActive}
          className={LABEL_HANDLE}
          listing={listing}
          onListingClicked={onListingClicked}
          mapComponentRefreshToken={mapComponentRefreshToken}
        />
      );
    }

    // Explicit type change to object literal for Google OverlayViews (geolocation is SDK type)
    const firstListing = ensureListing(listingArr[0]);
    const geolocation = firstListing.attributes.geolocation;
    const latLngLiteral = { lat: geolocation.lat, lng: geolocation.lng };

    return (
      <SearchMapGroupLabelWithOverlay
        key={listingArr[0].id.uuid}
        position={latLngLiteral}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        isActive={isActive}
        className={LABEL_HANDLE}
        listings={listingArr}
        onListingClicked={onListingClicked}
        mapComponentRefreshToken={mapComponentRefreshToken}
      />
    );
  });
  return priceLabels;
};

/**
 * MapWithGoogleMap uses withGoogleMap HOC.
 * It handles some of the google map initialization states.
 */
const CLUSTERER_COLOR = '#3643BA';

const MapWithGoogleMap = withGoogleMap(props => {
  const {
    center,
    infoCardOpen,
    onIdle,
    onMapLoad,
    onCloseMap,
    onZoomStore,
    zoom,
    providers,
    location,
    history
  } = props;
  const controlPosition =
    typeof window !== 'undefined' && typeof window.google !== 'undefined'
      ? window.google.maps.ControlPosition.RIGHT_BOTTOM
      : 5;

  const onPinClick = ({ lat, lng, id, countryId }) => {
    onZoomStore({ lat, lng });
    onCloseMap();

    history.push({
      pathname: location.pathname,
      search: `${stringify({ ...parse(location.search), providerId: id, countryId, page: 1 })}`,
    })
  }

  const createSvg = (color) => window.btoa(`
    <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
      <circle cx="120" cy="120" opacity=".6" r="70" />
      <circle cx="120" cy="120" opacity=".3" r="90" />
      <circle cx="120" cy="120" opacity=".2" r="110" />
      <circle cx="120" cy="120" opacity=".1" r="130" />
    </svg>`);

  const setCalculator = (markers, numStyles) => {
    return  {text: markers.length, index: 1 }
  }

  return (
    <GoogleMap
      defaultZoom={zoom}
      defaultCenter={center}
      options={{
        // Disable all controls except zoom
        mapTypeControl: false,
        scrollwheel: false,
        fullscreenControl: false,
        clickableIcons: false,
        streetViewControl: false,

        // When infoCard is open, we can't differentiate double click on top of card vs map.
        disableDoubleClickZoom: !!infoCardOpen,
        zoomControlOptions: {
          position: controlPosition,
        },

        //Map Style
        styles: mapStyle,
      }}
      ref={onMapLoad}
      onIdle={onIdle}
    >
      <MarkerClusterer
         clusterClass={css.markerClusterer}
         styles={[
          {textColor: 'white', height: 53, width: 53, url: `data:image/svg+xml;base64,${createSvg(CLUSTERER_COLOR)}`},
        ]}
         calculator={setCalculator}
      >
        {providers.map((provider, i) => {
            const {attributes: {name, location: locationProvider, isDecathlon, countryId}, id: {uuid}} = provider;
            const isProviderSelected = parse(location.search).providerId === uuid;
            const scaledSize = isProviderSelected ? { width: 28, height: 38 } : { width: 22, height: 32 };
            const logoSvg = isDecathlon ? pinDktLogo : pinExternalLogo;

            return <Marker
              key={`${name}${i}`}
              onClick={()=> onPinClick({ lat: locationProvider?.lat, lng: locationProvider?.lng, id: uuid, countryId })}
              position={{ lat: parseFloat(locationProvider?.lat), lng: parseFloat(locationProvider?.lng) }}
              icon={{ url: logoSvg, scale: scaledSize }}

            />
          })
        }
      </MarkerClusterer>
    </GoogleMap>
  );
});

class SearchMapWithGoogleMap extends Component {
  constructor(props) {
    super(props);
    this.map = null;
    this.viewportBounds = null;

    this.onMapLoad = this.onMapLoad.bind(this);
    this.onIdle = this.onIdle.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.location, this.props.location)) {
      // If no mapSearch url parameter is given, this is original location search
      const { mapSearch } = parse(this.props.location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });
      if (!mapSearch) {
        this.viewportBounds = null;
      }
    }

    if (this.map) {
      const currentBounds = getMapBounds(this.map);
      // Do not call fitMapToBounds if bounds are the same.
      // Our bounds are viewport bounds, and fitBounds will try to add margins around those bounds
      // that would result to zoom-loop (bound change -> fitmap -> bounds change -> ...)
      if (!isEqual(this.props.bounds, currentBounds)) {
        fitMapToBounds(this.map, this.props.bounds, { padding: 0 });
      }
    }
  }

  onMapLoad(map) {
    this.map = map;
    this.props.onMapLoad(map);
  }

  onIdle(e) {
    if (this.map) {
      // Let's try to find the map container element
      const mapContainer = this.props.containerElement.props.id
        ? document.getElementById(this.props.containerElement.props.id)
        : null;

      // If reusableMapHiddenHandle is given and parent element has that class,
      // we don't listen moveend events.
      // This fixes mobile Chrome bug that sends map events to invisible map components.
      const isHiddenByReusableMap =
        this.props.reusableMapHiddenHandle &&
        mapContainer &&
        mapContainer.parentElement.classList.contains(this.props.reusableMapHiddenHandle);

      if (!isHiddenByReusableMap) {
        const viewportMapBounds = getMapBounds(this.map);
        const viewportMapCenter = getMapCenter(this.map);
        const viewportBounds = viewportMapBounds
          ? sdkBoundsToFixedCoordinates(viewportMapBounds, BOUNDS_FIXED_PRECISION)
          : null;

        this.props.updateBounds(viewportBounds)
        this.viewportBounds = viewportBounds;
      }
    }
  }

  onZoomStore = ({ lat, lng }) => {
    this.map.fitBounds({ north: lat + 0.003, east: lng + 0.003, south: lat - 0.003, west: lng - 0.003 })
  }

  render() {
    const { onMapLoad, providers, location, history, bounds, origin, ...rest } = this.props;
    return <MapWithGoogleMap onMapLoad={this.onMapLoad} onIdle={this.onIdle} {...rest} onZoomStore={this.onZoomStore} providers={providers}  history={history} location={location} />;
  }
}

SearchMapWithGoogleMap.defaultProps = {
  center: new sdkTypes.LatLng(0, 0),
  infoCardOpen: null,
  listings: [],
  activeListingId: null,
  zoom: 11,
  reusableMapHiddenHandle: null,
};

export default SearchMapWithGoogleMap;
